import * as React from "react";
import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Row, Col } from "components/bootstrap";
import PaginatedEntityTable from "components/common/PaginatedEntityTable";
import QueryHelper from "components/common/QueryHelper";
import { ErrorsProvider } from "components/lookup-tables/contexts/ErrorsContext";
import { fetchPaginatedLookupTables, lookupTablesKeyFn } from "components/lookup-tables/hooks/useLookupTablesAPI";
import { lutListElements } from "./constants";
import columnRenderers from "./column-renderers";
import ErrorsConsumer from "./errors-consumer";
import useActions from "./use-actions";
const exampleQuery = "geoip";
const exampleFieldQuery = "title:geoip";
function LookupTableList() {
  const { t } = useTranslation("lookup_tables");
  const queryHelpComponent = /* @__PURE__ */ React.createElement(
    QueryHelper,
    {
      entityName: "lookup table",
      commonFields: ["id", "title", "name", "description"],
      example: /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "shared.query_example_intro", components: { fieldCode: /* @__PURE__ */ React.createElement("code", null) } }), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("kbd", null, exampleQuery), " ", /* @__PURE__ */ React.createElement("br", null), t("shared.query_example_same"), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("kbd", null, exampleFieldQuery))
    }
  );
  const [{ lutNames, cacheNames, adapterNames }, setNames] = useState({ lutNames: void 0, cacheNames: void 0, adapterNames: void 0 });
  const { renderActions } = useActions();
  const handleFetchTables = useCallback(async (searchParams) => {
    var _a;
    const resp = await fetchPaginatedLookupTables(searchParams);
    const overrides = {
      title: { sortable: true },
      name: { sortable: true },
      description: { sortable: false },
      cache_id: { sortable: false },
      data_adapter_id: { sortable: false }
    };
    const expectedIds = lutListElements.defaultLayout.defaultDisplayedAttributes;
    const attrMap = /* @__PURE__ */ new Map();
    ((_a = resp.attributes) != null ? _a : []).forEach((attr) => {
      var _a2;
      const override = (_a2 = overrides[attr.id]) != null ? _a2 : {};
      attrMap.set(attr.id, { ...attr, ...override });
    });
    expectedIds.forEach((id) => {
      var _a2;
      if (!attrMap.has(id)) {
        const override = (_a2 = overrides[id]) != null ? _a2 : {};
        attrMap.set(id, {
          id,
          title: id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          type: "STRING",
          ...override
        });
      }
    });
    const mergedAttributes = Array.from(attrMap.values());
    const patchedResponse = {
      ...resp,
      attributes: mergedAttributes
    };
    setNames({
      lutNames: patchedResponse.list.map((lut) => lut.name),
      cacheNames: Object.values(patchedResponse.meta.caches).map((cache) => cache.name),
      adapterNames: Object.values(patchedResponse.meta.adapters).map((adapter) => adapter.name)
    });
    return Promise.resolve(patchedResponse);
  }, []);
  return /* @__PURE__ */ React.createElement(ErrorsProvider, null, /* @__PURE__ */ React.createElement(ErrorsConsumer, { lutNames, cacheNames, adapterNames }), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    PaginatedEntityTable,
    {
      humanName: t("lut_list.human_name"),
      entityActions: renderActions,
      queryHelpComponent,
      tableLayout: lutListElements.defaultLayout,
      fetchEntities: handleFetchTables,
      keyFn: lookupTablesKeyFn,
      entityAttributesAreCamelCase: false,
      columnRenderers
    }
  ))));
}
export default LookupTableList;
